import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, onSnapshot, addDoc, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore'; // <-- Ajout de getDoc
import { Calendar, Loader2, Info, PlusCircle, Edit, Trash2, Printer } from 'lucide-react'; // <-- Ajout de Printer

const EmploiDuTempsManagement = ({ db, setError, setMessage, schoolId, userRole }) => {
    // --- Rôles et Permissions ---
    // Les rôles autorisés à modifier l'emploi du temps (EDIT_ROLES)
    const EDIT_ROLES = ['superAdmin', 'admin', 'secretaireGeneral', 'directeurPedagogique'];
    // Vérification simplifiée pour la gestion
    const canManageEdt = EDIT_ROLES.includes(userRole);
    
    const [classes, setClasses] = useState([]);
    const [matieres, setMatieres] = useState([]); // Pour le formulaire de saisie
    const [enseignants, setEnseignants] = useState([]); // Pour le formulaire de saisie

    const [selectedClassId, setSelectedClassId] = useState('');
    const [edtItems, setEdtItems] = useState({}); // Éléments d'emploi du temps groupés pour la classe sélectionnée
    const [loading, setLoading] = useState(true);
    const [loadingEdt, setLoadingEdt] = useState(false);

    // Suppression des états viewMode, selectedDate et weekRange.

    // États pour le formulaire de saisie EDT
    const [formMatiereId, setFormMatiereId] = useState('');
    const [formEnseignantId, setFormEnseignantId] = useState('');
    const [formHeureDebut, setFormHeureDebut] = useState('');
    const [formHeureFin, setFormHeureFin] = useState('');
    const [formJourSemaine, setFormJourSemaine] = useState(''); // Jour de la semaine pour la nouvelle entrée
    const [formSalle, setFormSalle] = useState('');
    const [isEditingEdtItem, setIsEditingEdtItem] = useState(false);
    const [currentEdtItemId, setCurrentEdtItemId] = useState(null);

    // Options pour les jours de la semaine
    const joursSemaineOptions = [
        { value: 'lundi', label: 'Lundi' },
        { value: 'mardi', label: 'Mardi' },
        { value: 'mercredi', label: 'Mercredi' },
        { value: 'jeudi', label: 'Jeudi' },
        { value: 'vendredi', label: 'Vendredi' },
        { value: 'samedi', label: 'Samedi' },
        { value: 'dimanche', label: 'Dimanche' },
    ];

    // Suppression de la fonction utilitaire getWeekBounds.

    // Charger les classes, matières et enseignants au montage du composant
    useEffect(() => {
        if (!db || !schoolId) {
            if (!db) console.error("Firestore DB instance is not provided.");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const [classesSnapshot, matieresSnapshot, enseignantsSnapshot] = await Promise.all([
                    getDocs(collection(db, 'schools', schoolId, 'classes')),
                    getDocs(collection(db, 'schools', schoolId, 'matieres')),
                    getDocs(collection(db, 'schools', schoolId, 'enseignants'))
                ]);

                setClasses(classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setMatieres(matieresSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setEnseignants(enseignantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) {
                console.error("Erreur lors du chargement des données de référence:", err);
                setError(`Erreur lors du chargement des données: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();

        // Suppression de l'initialisation de selectedDate
    }, [db, setError, schoolId]);

    // Charger l'emploi du temps lorsque la classe sélectionnée change
    useEffect(() => {
        if (!db || !selectedClassId || !schoolId) {
            setEdtItems({});
            // Suppression de setWeekRange
            return;
        }

        setLoadingEdt(true);
        setError('');
        setMessage({ type: '', text: '' });

        // 1. Déterminer la plage de dates pour l'affichage du contexte : toujours "Modèle Annuel Récurent"
        const rangeText = 'Modèle Annuel Récurent';
        // Suppression de setWeekRange
        
        // 2. Requête Firestore pour récupérer les éléments du modèle récurrent
        const edtQuery = query(
            collection(db, 'schools', schoolId, 'emploidutemps'),
            where('classeId', '==', selectedClassId),
            orderBy('heureDebut', 'asc')
        );

        const unsubscribe = onSnapshot(edtQuery, (snapshot) => {
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));

            // Regrouper par jour de la semaine
            const groupedByDay = items.reduce((acc, item) => {
                // Utiliser le label en français pour le regroupement/affichage
                const dayLabel = joursSemaineOptions.find(j => j.value === item.jourSemaine)?.label || item.jourSemaine;
                if (!acc[dayLabel]) {
                    acc[dayLabel] = [];
                }
                acc[dayLabel].push(item);
                return acc;
            }, {});

            setEdtItems(groupedByDay);
            
            // Simplification du message de succès
            const messageText = `Emploi du temps Modèle Annuel chargé pour la classe sélectionnée.`;
            
            setMessage({ type: 'success', text: messageText });
            setLoadingEdt(false);
        }, (err) => {
            console.error("Erreur lors du chargement de l'emploi du temps:", err);
            setError(`Erreur lors du chargement de l'emploi du temps: ${err.message}`);
            setLoadingEdt(false);
        });

        // Dépendances mises à jour
        return () => unsubscribe();
    }, [db, selectedClassId, setError, setMessage, schoolId]);


    const handleClassChange = (e) => {
        setSelectedClassId(e.target.value);
        resetForm();
    };

    // Suppression de handleDateChange

    // Fonction pour réinitialiser le formulaire
    const resetForm = () => {
        setFormMatiereId('');
        setFormEnseignantId('');
        setFormHeureDebut('');
        setFormHeureFin('');
        setFormJourSemaine('');
        setFormSalle('');
        setIsEditingEdtItem(false);
        setCurrentEdtItemId(null);
    };

    // Fonction pour gérer l'ajout/mise à jour d'un élément EDT
    const handleSaveEdtItem = async (e) => {
        e.preventDefault();
        if (!selectedClassId || !formMatiereId || !formEnseignantId || !formHeureDebut || !formHeureFin || !formJourSemaine || !formSalle) {
            setError('Veuillez remplir tous les champs du formulaire pour ajouter/modifier un élément d\'emploi du temps.');
            return;
        }

        // Récupérer les noms complets pour la sauvegarde
        const matiereNom = matieres.find(m => m.id === formMatiereId)?.name || '';
        const enseignantNom = enseignants.find(ens => ens.id === formEnseignantId);
        const enseignantNomComplet = enseignantNom ? `${enseignantNom.prenom} ${enseignantNom.nom}` : '';

        const itemData = {
            classeId: selectedClassId,
            matiereId: formMatiereId,
            matiere: matiereNom,
            enseignantId: formEnseignantId,
            enseignant: enseignantNomComplet,
            heureDebut: formHeureDebut,
            heureFin: formHeureFin,
            jourSemaine: formJourSemaine,
            salle: formSalle,
            schoolId: schoolId,
            // Ces éléments représentent le MODÈLE RÉCURRENT hebdomadaire de la classe.
        };

        try {
            if (isEditingEdtItem) {
                const itemRef = doc(db, 'schools', schoolId, 'emploidutemps', currentEdtItemId);
                await updateDoc(itemRef, itemData);
                setMessage({ type: 'success', text: 'Élément d\'emploi du temps mis à jour avec succès !' });
            } else {
                await addDoc(collection(db, 'schools', schoolId, 'emploidutemps'), itemData);
                setMessage({ type: 'success', text: 'Élément d\'emploi du temps ajouté avec succès !' });
            }
            resetForm(); // Réinitialiser le formulaire après succès
        } catch (err) {
            console.error("Erreur lors de la sauvegarde de l'élément d'emploi du temps:", err);
            setError(`Erreur lors de la sauvegarde: ${err.message}`);
        }
    };

    // Fonction pour charger les données d'un élément EDT dans le formulaire pour l'édition
    const handleEditEdtItem = (item) => {
        setFormMatiereId(item.matiereId);
        setFormEnseignantId(item.enseignantId);
        setFormHeureDebut(item.heureDebut);
        setFormHeureFin(item.heureFin);
        setFormJourSemaine(item.jourSemaine);
        setFormSalle(item.salle);
        setIsEditingEdtItem(true);
        setCurrentEdtItemId(item.id);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Remonter en haut pour le formulaire
    };

    // Fonction pour supprimer un élément EDT
    const handleDeleteEdtItem = async (itemId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément de l\'emploi du temps ? Cela affectera le modèle récurrent.')) {
            return;
        }
        try {
            await deleteDoc(doc(db, 'schools', schoolId, 'emploidutemps', itemId));
            setMessage({ type: 'success', text: 'Élément d\'emploi du temps supprimé avec succès !' });
        } catch (err) {
            console.error("Erreur lors de la suppression de l'élément d'emploi du temps:", err);
            setError(`Erreur lors de la suppression: ${err.message}`);
        }
    };

    // NOUVELLE FONCTION : Gestion de l'impression
    const handlePrintEdt = async () => {
        if (!selectedClassId || Object.keys(edtItems).length === 0) {
            setError("Veuillez sélectionner une classe avec un emploi du temps pour imprimer.");
            return;
        }

        // 1. Récupérer les informations de l'école
        let schoolInfo = { name: 'École (Nom à configurer)', address: 'Adresse à configurer', phone: 'Téléphone à configurer' };
        
        try {
            if (schoolId) {
                 const schoolRef = doc(db, 'schools', schoolId);
                 const schoolSnap = await getDoc(schoolRef);
                 if (schoolSnap.exists()) {
                     const data = schoolSnap.data();
                     schoolInfo = {
                         name: data.name || schoolInfo.name, // Adapter au champ réel dans votre collection 'schools'
                         address: data.adresse_ecole || schoolInfo.address,
                         phone: data.telephone_ecole || schoolInfo.phone,
                     };
                 }
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des infos de l'école pour l'impression:", error);
        }

        const selectedClass = classes.find(cls => cls.id === selectedClassId);
        const className = selectedClass?.nom || 'Classe Inconnue';

        // 2. Construire le contenu HTML du tableau
        let edtHtmlContent = '';

        // Trier les jours dans l'ordre de la semaine et filtrer ceux qui ont des entrées
        const sortedDayNames = joursSemaineOptions
            .map(j => j.label)
            .filter(label => edtItems[label]); 

        sortedDayNames.forEach(dayName => {
            const items = edtItems[dayName].sort((a, b) => {
                // Tri par heure de début
                const timeA = a.heureDebut.split(':').map(Number);
                const timeB = b.heureDebut.split(':').map(Number);
                if (timeA[0] !== timeB[0]) return timeA[0] - timeB[0];
                return timeA[1] - timeB[1];
            });

            if (items.length > 0) {
                edtHtmlContent += `
                    <tr>
                        <td colspan="4" class="day-header">${dayName}</td>
                    </tr>
                `;
                items.forEach(item => {
                    edtHtmlContent += `
                        <tr>
                            <td>${item.heureDebut} - ${item.heureFin}</td>
                            <td>${item.matiere}</td>
                            <td>${item.enseignant}</td>
                            <td>${item.salle}</td>
                        </tr>
                    `;
                });
            }
        });

        // 3. Créer le document HTML complet avec styles CSS pour l'impression
        const html = `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Emploi du Temps - ${className}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
                    .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #007bff; padding-bottom: 10px; }
                    .header h1 { margin: 0; font-size: 24px; color: #007bff; }
                    .header p { margin: 2px 0; font-size: 12px; }
                    .title { text-align: center; margin-bottom: 15px; font-size: 18px; font-weight: bold; }
                    table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                    th, td { border: 1px solid #ccc; padding: 8px 10px; text-align: left; font-size: 14px; }
                    th { background-color: #e9ecef; font-weight: bold; color: #333; }
                    .day-header { background-color: #007bff; color: white; font-weight: bold; text-align: center !important; font-size: 16px; padding: 10px !important; }
                    @media print {
                        body { font-size: 10pt; }
                        .header { border-bottom: 2px solid #007bff; }
                        table { page-break-inside: auto; }
                        tr { page-break-inside: avoid; page-break-after: auto; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>${schoolInfo.name}</h1>
                    <p>${schoolInfo.address}</p>
                    <p>Tél: ${schoolInfo.phone}</p>
                </div>

                <div class="title">Emploi du Temps (Modèle Annuel Récurent) pour la Classe: ${className}</div>

                <table>
                    <thead>
                        <tr>
                            <th>Heure</th>
                            <th>Matière</th>
                            <th>Enseignant</th>
                            <th>Salle</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${edtHtmlContent}
                    </tbody>
                </table>
                <div style="margin-top: 30px; font-size: 10px; text-align: right;">Imprimé le ${new Date().toLocaleDateString('fr-FR')}</div>
            </body>
            </html>
        `;

        // 4. Ouvrir la nouvelle fenêtre, y insérer le contenu et imprimer
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(html);
            printWindow.document.close();
            // Laissez un court instant pour que le contenu se charge avant l'impression
            setTimeout(() => {
                printWindow.print();
            }, 500);
        } else {
            setError("Impossible d'ouvrir la fenêtre d'impression. Veuillez vérifier les bloqueurs de pop-up.");
        }
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center p-6 bg-white rounded-lg shadow-md min-h-[200px]">
                <Loader2 className="animate-spin text-blue-500 mr-3" size={24} />
                <p className="text-gray-700">Chargement des données de base (classes, matières, enseignants)...</p>
            </div>
        );
    }

    // Blocage si superAdmin n'a pas sélectionné d'école
    if (userRole === 'superAdmin' && !schoolId) {
        return (
            <div className="p-4 bg-gray-50 rounded-lg shadow-md">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                    <Calendar size={28} className="mr-3 text-purple-600" /> Gestion des Emplois du Temps
                </h2>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 mb-4">
                    <p className="font-semibold">Veuillez sélectionner une école dans le tableau de bord pour gérer les emplois du temps.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 bg-white rounded-lg shadow-md mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                <Calendar size={28} className="mr-3" /> Gestion des Emplois du Temps (Modèle Annuel Récurent)
            </h2>

            {/* Section de filtre par classe */}
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">Filtrer l'Emploi du Temps</h3>
                
                <div className="grid grid-cols-1 gap-4"> {/* Simplification du grid */}
                    <div>
                        <label htmlFor="select-class" className="block text-gray-700 font-medium mb-1">
                            Sélectionner une Classe:
                        </label>
                        <select
                            id="select-class"
                            className="p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 w-full"
                            value={selectedClassId}
                            onChange={handleClassChange}
                        >
                            <option value="">-- Choisissez une classe --</option>
                            {classes.map(cls => (
                                <option key={cls.id} value={cls.id}>{cls.nom} {cls.niveau ? `(${cls.niveau})` : ''}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="mt-4 p-3 bg-blue-100 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800 font-semibold flex items-center">
                        <Info size={18} className="mr-2" />
                        Affichage du **Modèle Annuel Récurent**. Ces entrées définissent l'emploi du temps standard de la classe chaque semaine.
                    </p>
                </div>
            </div>

            {/* Formulaire de saisie/édition d'un élément d'emploi du temps (Visible pour les rôles administratifs) */}
            {canManageEdt && selectedClassId && (
                <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
                    <h3 className="text-xl font-semibold text-blue-800 mb-4 flex items-center">
                        <PlusCircle size={22} className="mr-2" /> {isEditingEdtItem ? 'Modifier un élément' : 'Ajouter un élément'} au Modèle
                    </h3>
                    <form onSubmit={handleSaveEdtItem} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label htmlFor="formMatiereId" className="block text-gray-700 text-sm font-medium mb-1">Matière</label>
                            <select
                                id="formMatiereId"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={formMatiereId}
                                onChange={(e) => setFormMatiereId(e.target.value)}
                                required
                            >
                                <option value="">-- Choisir une matière --</option>
                                {matieres.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="formEnseignantId" className="block text-gray-700 text-sm font-medium mb-1">Enseignant</label>
                            <select
                                id="formEnseignantId"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={formEnseignantId}
                                onChange={(e) => setFormEnseignantId(e.target.value)}
                                required
                            >
                                <option value="">-- Choisir un enseignant --</option>
                                {enseignants.map(ens => (
                                    <option key={ens.id} value={ens.id}>{ens.prenom} {ens.nom}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="formJourSemaine" className="block text-gray-700 text-sm font-medium mb-1">Jour de la Semaine</label>
                            <select
                                id="formJourSemaine"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={formJourSemaine}
                                onChange={(e) => setFormJourSemaine(e.target.value)}
                                required
                            >
                                <option value="">-- Choisir un jour --</option>
                                {joursSemaineOptions.map(jour => (
                                    <option key={jour.value} value={jour.value}>{jour.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="formHeureDebut" className="block text-gray-700 text-sm font-medium mb-1">Heure de Début</label>
                            <input
                                type="time"
                                id="formHeureDebut"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={formHeureDebut}
                                onChange={(e) => setFormHeureDebut(e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="formHeureFin" className="block text-gray-700 text-sm font-medium mb-1">Heure de Fin</label>
                            <input
                                type="time"
                                id="formHeureFin"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={formHeureFin}
                                onChange={(e) => setFormHeureFin(e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="formSalle" className="block text-gray-700 text-sm font-medium mb-1">Salle</label>
                            <input
                                type="text"
                                id="formSalle"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={formSalle}
                                onChange={(e) => setFormSalle(e.target.value)}
                                required
                            />
                        </div>
                        <div className="md:col-span-full text-right flex justify-end space-x-2 mt-4">
                            {isEditingEdtItem && (
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold flex items-center justify-center transition-colors duration-200"
                                >
                                    Annuler
                                </button>
                            )}
                            <button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-semibold flex items-center justify-center transition-colors duration-200"
                            >
                                {isEditingEdtItem ? <Edit size={20} className="mr-2" /> : <PlusCircle size={20} className="mr-2" />}
                                {isEditingEdtItem ? 'Mettre à jour' : 'Ajouter l\'élément'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Affichage de l'emploi du temps */}
            {loadingEdt ? (
                <div className="flex items-center justify-center p-6 bg-white rounded-lg shadow-md min-h-[200px]">
                    <Loader2 className="animate-spin text-blue-500 mr-3" size={24} />
                    <p className="text-gray-700">Chargement de l'emploi du temps...</p>
                </div>
            ) : !selectedClassId ? (
                <div className="p-4 bg-blue-100 border-l-4 border-blue-500 text-blue-800 rounded-lg shadow-md">
                    <p className="flex items-center">
                        <Info size={20} className="mr-2" />
                        Veuillez sélectionner une classe pour afficher le modèle d'emploi du temps annuel.
                    </p>
                </div>
            ) : Object.keys(edtItems).length === 0 ? (
                <div className="p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 rounded-lg shadow-md">
                    <p className="flex items-center">
                        <Info size={20} className="mr-2" />
                        Aucun emploi du temps (modèle annuel) trouvé pour cette classe. { canManageEdt && "Ajoutez-en un ci-dessus !" }
                    </p>
                </div>
            ) : (
                <div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        Emploi du Temps pour {classes.find(cls => cls.id === selectedClassId)?.nom || 'N/A'} - Modèle Annuel Récurent
                    </h3>
                    
                    {/* BOUTON D'IMPRESSION AJOUTÉ ICI */}
                    <button
                        onClick={handlePrintEdt}
                        className="mb-4 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-semibold flex items-center transition-colors duration-200 shadow-md"
                        disabled={Object.keys(edtItems).length === 0}
                    >
                        <Printer size={20} className="mr-2" /> Imprimer l'Emploi du Temps
                    </button>
                    <hr className="mb-4" />
                    {/* FIN BOUTON D'IMPRESSION */}

                    {Object.keys(edtItems).sort((a, b) => {
                        // Tri des jours de la semaine (pour un affichage cohérent)
                        const daysOrder = joursSemaineOptions.map(j => j.label.toLowerCase());
                        return daysOrder.indexOf(a.toLowerCase()) - daysOrder.indexOf(b.toLowerCase());
                    }).map(dayName => (
                        <div key={dayName} className="mb-6 p-4 border rounded-lg bg-white shadow-sm">
                            <h4 className="text-lg font-bold text-blue-700 mb-3 capitalize">{dayName}</h4>
                            {edtItems[dayName].length > 0 ? (
                                        <ul className="space-y-2">
                                        {edtItems[dayName].sort((a, b) => {
                                            // Tri par heure de début
                                            const timeA = a.heureDebut.split(':').map(Number);
                                            const timeB = b.heureDebut.split(':').map(Number);
                                            if (timeA[0] !== timeB[0]) return timeA[0] - timeB[0];
                                            return timeA[1] - timeB[1];
                                        }).map(item => (
                                            <li key={item.id} className="bg-gray-50 p-3 rounded-md flex items-center justify-between">
                                                <div>
                                                    <p className="font-semibold text-gray-800">
                                                        <span className="text-blue-700">{item.heureDebut} - {item.heureFin}</span> : {item.matiere} ({item.enseignant}) - Salle: {item.salle}
                                                    </p>
                                                </div>
                                                {/* Visibilité des actions pour les rôles administratifs */}
                                                {canManageEdt && (
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleEditEdtItem(item)}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                            title="Éditer"
                                                        >
                                                            <Edit size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteEdtItem(item.id)}
                                                            className="text-red-600 hover:text-red-900"
                                                            title="Supprimer"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>

                            ) : (
                                <p className="text-gray-600">Pas d'activités prévues pour ce jour.</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EmploiDuTempsManagement;